import React, { useEffect, useState } from 'react'
import RightPanel from 'components/RightPanel/RightPanel'
import { useParams, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import AdminPanelCN from 'components/AdminPanelCN/AdminPanelCN'
import HeaderCN from 'components/HeaderCN/HeaderCN'
import { AssetPresentationsMenu } from 'features/Presentations/components/AssetPresentationsMenu'
import { AssetReportsButton } from 'features/Explorer/components/AssetReportsButton'
import SecondaryNavCN from 'components/SecondaryNavCN/SecondaryNavCN'
import { Avatar, AvatarImage, AvatarFallback } from 'components/ui/avatar'
import Popup from 'components/Popup'
import MapLocationPopupCN from 'components/popups/MapLocationPopupCN'
import ProjectActionSheet from './components/Projects/ProjectActionSheet'
import RenderSubRoutes from 'components/RenderSubRoutes'
import SidebarLocationProjectRecent from '../../components/Sidebars/SidebarLocationProjectRecent'
import SidebarLocationProjectPartners from '../../components/Sidebars/SidebarLocationProjectPartners'
import getProjectRoutes from './getRoutes/getProjectRoutes'
import routes from 'constants/routes'
import useMediaQuery from 'hooks/useMediaQuery'
import useCapabilities from 'hooks/useCapabilities'
import * as _ from 'styles/helpers'
import { initializeApiKey } from 'slices/apiKeysSlice'
import { initializeActiveLocations, patchLocation } from 'slices/locationSlice'
import { initializeUsers } from 'slices/usersSlice'
import { initializeMyProjects, initializeProjects, patchProject } from 'slices/projectSlice'

const Projects_ShowCN = () => {
	const dispatch = useDispatch()
	const params = useParams()
	const { id } = params

	const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
	const [backTitle, setBackTitle] = useState(null)
	const [backLink, setBackLink] = useState(null)
	const [openModifyPopup, setOpenModifyPopup] = useState(false)
	const [openMap, setOpenMap] = useState(false)
	const [center, setCenter] = useState<number[]>([])
	const [mapTitle, setMapTitle] = useState<string | null>(null)
	const [mapRecord, setMapRecord] = useState<any>(null)

	const currentRoute = useLocation()
	const getBackLink = () => {
		if (currentRoute.state) {
			if (backTitle === null) {
				setBackTitle(currentRoute.state.backButtonTitle)
				setBackLink(currentRoute.state.backButtonLink)
			}
			return { title: currentRoute.state.backButtonTitle, link: currentRoute.state.backButtonLink }
		}
		if (backTitle !== null) {
			return { title: backTitle, link: backLink }
		}
		return { title: 'Projects', link: routes.PROJECTS }
	}

	const { entity: apiKey } = useSelector((state: any) => state.apiKey)

	const userLogin = useSelector((state: any) => state.userLogin)
	const { userInfo } = userLogin
	const capabilities = useCapabilities()

	const { projectLoading, projectInitialized, project } = useSelector((state: any) => ({
		projectLoading: state.projects.isLoading,
		projectInitialized: state.projects.isInitialized,
		project: state.projects.entities[id],
	}))

	const locationId = project?.projectAsset?._id || id
	const { isLoading, isInitialized, location } = useSelector((state: any) => ({
		isLoading: state.locations.isLoading,
		isInitialized: state.locations.isInitialized,
		location: state.locations.entities[locationId],
	}))

	const { success: userListInitialized } = useSelector((state: any) => ({
		success: state.userList.isInitialized,
	}))

	useEffect(() => {
		dispatch(initializeActiveLocations())
		if (!userListInitialized) {
			dispatch(initializeUsers())
		}
		dispatch(initializeMyProjects())
		dispatch(initializeApiKey('REACT_APP_GOOGLE_API_KEY'))
	}, [dispatch, id, isInitialized, projectInitialized, userListInitialized])

	const openProjectEdit = () => {
		setOpenModifyPopup(true)
	}

	const handleEditProject = async (newProject: any) => {
		await dispatch(
			patchProject({
				projectId: newProject._id,
				updates: {
					projectName: newProject.projectName,
					projectDescription: newProject.projectDescription,
					projectType: newProject.projectType,
					isActive: newProject.isActive,
					image: location?.image,
					updateNote: 'Project edited.',
				},
			}),
		)
		dispatch(
			initializeProjects({
				locationId,
			}),
		)
		dispatch(patchLocation({ locationId, updates: { updateNote: 'Project Edited' } }))
	}

	const openInMap = () => {
		if (!location) return
		setCenter([location.latitude, location.longitude])
		setMapRecord(location)
		if (location.retailer) {
			setMapTitle(`${location.retailer} - ${location.assetName} - ${location.address}, ${location.city}, ${location.stateProvince}`)
		} else {
			setMapTitle(`${location.assetName} - ${location.address}, ${location.city}, ${location.stateProvince}`)
		}
		setOpenMap(true)
	}

	const partners = project?.partners
	const assetOwner = project?.owner
	const isMobile = useMediaQuery(_.MQ.before_nav)

	if (!project) {
		return null
	}

	const subRoutes = getProjectRoutes(id, capabilities)

	return (
		<div className="flex h-screen overflow-hidden bg-gray-50">
			<AdminPanelCN mobileOpen={mobileMenuOpen} onMobileClose={() => setMobileMenuOpen(false)} />

			<div className="flex-1 flex flex-col overflow-hidden">
				<HeaderCN onMobileMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

				<div className="flex-shrink-0 border-b border-gray-200 bg-white px-6 py-2 flex items-center gap-3">
					<Avatar className="h-8 w-8">
						<AvatarImage src={location?.image} alt={project?.projectName} />
						<AvatarFallback className="text-xs bg-gray-200 text-gray-600">
							{(project?.projectName || '?').charAt(0).toUpperCase()}
						</AvatarFallback>
					</Avatar>
					{project?.owner?._id === userInfo?._id ? (
						<button
							type="button"
							onClick={openProjectEdit}
							className="font-semibold text-sm text-[#0080fe] bg-transparent border-none p-0 m-0 cursor-pointer hover:underline"
						>
							{project?.projectName}
						</button>
					) : (
						<span className="font-semibold text-sm text-gray-800">{project?.projectName}</span>
					)}
					{location && (
						<button
							type="button"
							onClick={openInMap}
							className="text-sm text-[#0080fe] bg-transparent border-none p-0 m-0 cursor-pointer hover:underline"
						>
							{location?.address}, {location?.city}, {location?.stateProvince}
						</button>
					)}
					<span className="text-sm text-gray-500">Project Originator: {project?.owner?.name}</span>
					{/* Decks with a story about this project (MYD-497). Renders nothing when there are none. */}
					<div className="ml-auto flex items-center gap-2">
						{/* Reports from a template, narrowed to this project (MYD-598). */}
						<AssetReportsButton type="project" entityId={id} name={project?.projectName} />
						<AssetPresentationsMenu scope="project" entityId={id} />
					</div>
				</div>

				<SecondaryNavCN
					links={subRoutes}
					backLink={getBackLink()}
					sourceId={null}
					sourceName="Projects"
				/>

				<div className="flex-1 flex overflow-hidden">
					<main className="flex-1 overflow-y-auto p-6">
						<RenderSubRoutes routes={subRoutes} state={currentRoute.state} />
					</main>

					<RightPanel storageKey="project" label="Project panel" className="overflow-hidden">
						{project && (
							<>
								<SidebarLocationProjectPartners
									location={location}
									project={project}
									assetOwner={assetOwner}
									assetName={location?.assetName}
									projectPartners={partners}
									userId={userInfo._id}
								/>
								<SidebarLocationProjectRecent project={project} />
							</>
						)}
					</RightPanel>
				</div>
			</div>

			<ProjectActionSheet
				project={project}
				open={openModifyPopup}
				onClose={() => setOpenModifyPopup(false)}
				onEdit={handleEditProject}
				showDelete={false}
			/>
			{openMap && mapRecord && (
				<MapLocationPopupCN
					open={openMap}
					onClose={() => setOpenMap(false)}
					apiKey={apiKey?.key}
					location={mapRecord}
				/>
			)}
		</div>
	)
}

export default Projects_ShowCN
